// ── ClientApprovalsSettingsPage ─────────────────────────────────────────────
//
// #3185 APPROVE.2 PR 2c (recon §6) — Settings → Client approvals: the
// workspace's default for which artefact types need client approval.
// GET/PUT /tenants/:id/client-approval-defaults (PR 2b), admin rank only
// (TENANT_ADMIN or legacy ADMIN, isAdminRole). Non-admins are sent back to
// /settings, as CustodyTransferPage does. A row left on the platform default
// is stored as no row (PUT null); ruling D9: the workspace and platform
// defaults only apply to projects with a client stakeholder.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import ClientApprovalMatrixTable from '../components/clientApprovals/ClientApprovalMatrixTable';
import { apiFetch } from '../lib/api';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';
import { D9_COPY, clientApprovalTypeLabel, platformDefaultRequired, requiredLabel } from '../lib/clientApprovals';
import PreEnforcementNotice from '../components/clientApprovals/PreEnforcementNotice';

type Matrix = Record<string, boolean>;
type Choice = 'default' | 'required' | 'not_required';

const toChoice = (v: boolean | undefined): Choice => (v === undefined ? 'default' : v ? 'required' : 'not_required');
const fromChoice = (c: Choice): boolean | null => (c === 'default' ? null : c === 'required');

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
  padding: 20, marginBottom: 16,
};
const selectStyle: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
  background: 'var(--surface-alt)', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: 13,
};

export default function ClientApprovalsSettingsPage() {
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const navigate = useNavigate();
  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);

  const [types, setTypes] = useState<string[]>([]);
  const [saved, setSaved] = useState<Matrix>({});
  const [draft, setDraft] = useState<Record<string, Choice>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (!tenantLoading && !isAdmin) navigate('/settings');
  }, [tenantLoading, isAdmin, navigate]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await apiFetch<{ defaults: Matrix; types: string[] }>(`/tenants/${tenantId}/client-approval-defaults`);
      setTypes(data.types);
      setSaved(data.defaults);
      setDraft(Object.fromEntries(data.types.map((t) => [t, toChoice(data.defaults[t])])));
    } catch (err) {
      setLoadError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId && isAdmin) void load();
  }, [tenantId, isAdmin, load]);

  // Only the rows that differ from what is stored are sent.
  const changes = useMemo(() => {
    const out: Record<string, boolean | null> = {};
    for (const t of types) {
      if (draft[t] !== undefined && draft[t] !== toChoice(saved[t])) out[t] = fromChoice(draft[t]);
    }
    return out;
  }, [types, draft, saved]);
  const dirty = Object.keys(changes).length > 0;

  const handleSave = async () => {
    if (!tenantId || !dirty) return;
    setSaving(true);
    setSaveError(null);
    setSavedFlash(false);
    try {
      const data = await apiFetch<{ defaults: Matrix }>(`/tenants/${tenantId}/client-approval-defaults`, {
        method: 'PUT',
        body: JSON.stringify({ defaults: changes }),
      });
      setSaved(data.defaults);
      setDraft(Object.fromEntries(types.map((t) => [t, toChoice(data.defaults[t])])));
      setSavedFlash(true);
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (tenantLoading || !isAdmin || !tenantId) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
        <Sidebar />
        <main style={{ flex: 1, padding: 32 }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar />
      <main style={{ flex: 1, padding: 32, maxWidth: 760 }}>
        <div style={{ marginBottom: 16 }}>
          <Link to="/settings" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}>
            ← Back to settings
          </Link>
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>
          Client approvals
        </h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
          Choose which deliverables need your client&rsquo;s approval in this workspace. A project can override these.
        </p>
        <p data-testid="client-approvals-d9" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 24px' }}>
          {D9_COPY}
        </p>
        <PreEnforcementNotice />

        <div style={cardStyle}>
          {loading ? (
            <div style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>Loading…</div>
          ) : loadError ? (
            <div role="alert" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: '#dc2626' }}>
              Couldn&rsquo;t load client approval settings: {loadError}
            </div>
          ) : (
            <>
              <ClientApprovalMatrixTable
                testId="client-approvals-matrix"
                types={types}
                hint={(t) => (saved[t] !== undefined
                  ? 'Set for this workspace'
                  : `Platform default: ${platformDefaultRequired(t) ? 'required when the project has a client' : 'not required'}`)}
                renderControl={(t) => (
                  <select
                    aria-label={`Client approval for ${clientApprovalTypeLabel(t)}`}
                    data-testid={`matrix-select-${t}`}
                    value={draft[t] ?? 'default'}
                    onChange={(e) => { setDraft((d) => ({ ...d, [t]: e.target.value as Choice })); setSavedFlash(false); }}
                    // Codex R2 finding 4: locked while saving, so the response can't overwrite a later edit.
                    disabled={saving}
                    style={selectStyle}
                  >
                    <option value="default">Platform default ({requiredLabel(platformDefaultRequired(t))})</option>
                    <option value="required">Required</option>
                    <option value="not_required">Not required</option>
                  </select>
                )}
              />
              {saveError && (
                <div role="alert" style={{ marginTop: 12, fontFamily: 'var(--font-body)', fontSize: 13, color: '#dc2626' }}>
                  {saveError}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!dirty || saving}
                  style={{
                    padding: '8px 16px', borderRadius: 'var(--radius-sm)', border: 'none',
                    background: 'var(--primary)', color: 'white', fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
                    cursor: !dirty || saving ? 'not-allowed' : 'pointer', opacity: !dirty || saving ? 0.5 : 1,
                  }}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
                {savedFlash && <span role="status" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: '#16a34a' }}>Saved.</span>}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
